<template>
    <div class="form-input" :class="[
                sizeableString ? 'form-input-' + sizeableString : '', {
                'is-active': isActive,
                'is-required': isRequired,
                'is-empty': isEmpty && !placeholderText,
                'has-error': hasError,
                'has-warning': !hasError && props.warning,
                'is-inverted': props.inverted,
                'is-plain': props.plain,
                'has-suffix': hasSuffix,
                'has-prefix': hasPrefix,
            }]">
        <div class="form-input__content">
            <div v-if="hasPrefix" class="form-input__prefix">
                <Icon v-if="props.icon" :icon="props.icon"/>
                <cdn-image v-if="previewSrc" :src="previewSrc" :size="{width: 24, ratio: 1}" class="h-24 w-24" rounded cover/>
            </div>
            <div class="form-input__field-wrapper">
                <span v-if="props.label" class="form-input__label" @click="handleFocus">{{props.label}}</span>

                <div class="form-input__field cursor-pointer" ref="inputWrapperElement" @focus="handleFocus" tabindex="0">
                    <div v-if="displayValue" class="truncate">
                        <template v-if="props.multiple">
                            <chip v-for="(displayValueItem, displayValueItemIndex) in displayValue" :key="displayValueItemIndex" small class="mr-4">
                                {{ displayValueItem }}
                            </chip>
                        </template>
                        <span v-else class="truncate">{{ displayValue }}</span>
                    </div>
                    <span v-else-if="placeholderText" class="form-input__placeholder">{{ placeholderText }}</span>
                </div>

                <input class="none" ref="inputElement" :enterkeyhint="props.enterkeyhint ?? 'next'" type="file" :autofocus="props.autofocus"
                       :accept="props.onlyImages ? 'image/png, image/gif, image/jpeg, image/jpg' : null"
                       :multiple="props.multiple"
                       :name="props.name"
                       @change="handleFileSelection" :disabled="props.disabled"/>
            </div>
            <div v-if="hasSuffix" class="form-input__suffix">
				<div v-if="isActive || submitValue" @click.stop="blurReset">
					<Icon :icon="icons.reset" class="cursor-pointer"/>
				</div>
                <tooltip v-else-if="props.help" :text="props.help" left>
                    <Icon :icon="icons.help"/>
                </tooltip>
            </div>
        </div>
        <div v-if="!hideDetails" class="form-input__details">
            <span v-if="hasError && errorMessage" class="form-input__message" tabindex="-1">{{ errorMessage }}</span>
            <span v-else-if="props.warning" class="form-input__message" tabindex="-1">{{ props.warning }}</span>
            <span v-else-if="props.info" class="form-input__message" tabindex="-1">{{ props.info }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {EXISTING_FORM_INPUT_VALUE} from "../../../config";
	import {helpIcon, resetIcon} from "../../../config";
	import {TextInputComponentEmits, type TextInputComponentProps, useTextInputComponent} from "../../../composables/useTextInputComponent";
	import {ref, watch, computed, nextTick} from "vue";
	import type {Ref} from "vue";
	import {useI18n} from "#imports";
	import {NativeApp} from "../../../services";

	const props = withDefaults(defineProps<{
		// Whether multiple-select is activated
		multiple?: boolean,

		// Whether to only allows the upload of images
		// Enables the native image picker
		onlyImages?: boolean,
	} & TextInputComponentProps>(), {});

	const emit = defineEmits(TextInputComponentEmits);

	const {t} = useI18n();

	const icons = {
		reset: resetIcon,
		help: helpIcon,
	};

	const inputElement: Ref<HTMLElement | null> = ref(null);
	const inputWrapperElement: Ref<HTMLElement | null> = ref(null);

	const {
		submitValue,
		sizeableString,
		isActive,
		isRequired,
		isEmpty,
		hasError,
		errorMessage,
		handleBlur,
		handleFocus,
		validate,
		reset
	} = useTextInputComponent(inputElement, props, emit);

	const displayValue = ref(null);
	const previewSrc = ref(null);
	const expectsInput = ref(false);

	const hasSuffix = computed(() => {
		return !!props.help || submitValue.value || isActive.value
	});

	const hasPrefix = computed(() => {
		return !!props.icon || !!previewSrc.value
	});

	const placeholderText = computed(() => {
		return props.placeholder ?? (props.rules?.required ? t('placeholder.select') : null)
	});

	watch(isActive, (newValue, oldValue) => {
		if (newValue && !oldValue) {
			openPicker();
		}
	});

	watch(submitValue, (newValue) => {
		if (!newValue) {
			displayValue.value = null;
			previewSrc.value = null;
		} else {
			if (props.multiple) {
				displayValue.value = submitValue.value.map(val => {
					return parseDisplayValue(val)
				});
				previewSrc.value = null;
			} else {
				displayValue.value = parseDisplayValue(submitValue.value)
				refreshPreviewSrc()
			}
		}
	}, {immediate: true});

	async function openPicker() {
		// Required to remove the focus after a file was selected or the picker was canceled, and to open the picker again afterwards
		document.body.onfocus = () => {
			setTimeout(() => {
				handleBlur();
				if (inputWrapperElement.value) {
					inputWrapperElement.value.blur();
				}
				document.body.onfocus = null;
			}, 5);
		};

		if (props.onlyImages && NativeApp.isNativeApp()) {
			await NativeApp.requestPermission('camera', (result) => {
				if (result) {
					setTimeout(() => {
						inputElement.value.click();
					}, 10); // Just a little timeout to let the permissions propagate on Android
				}
			});
		} else {
			inputElement.value.click();
		}
	}

	function blurReset() {
		reset();
		handleBlur();
	}

	function handleFileSelection(event) {
		expectsInput.value = false;
		if (event.target.files && event.target.files.length) {
			if (!props.multiple) {
				submitValue.value = event.target.files[0];
			} else {
				submitValue.value = event.target.files
			}
		}

		handleBlur();
	}

	function parseDisplayValue(value) {
		if (value === EXISTING_FORM_INPUT_VALUE) {
			return t('label.specified');
		}
		if (typeof File !== 'undefined' && value instanceof File) {
			return value.name;
		}
		if (value instanceof Object) {
			return value.path;
		}
		return value;
	}

	function refreshPreviewSrc() {
		if (typeof File !== 'undefined' && submitValue.value instanceof File && typeof displayValue.value === 'string' && (displayValue.value.endsWith('jpg') || displayValue.value.endsWith('png'))) {
			const reader = new FileReader();
			reader.onload = () => {
				previewSrc.value = reader.result;
			};

			reader.readAsDataURL(submitValue.value);
		} else {
			previewSrc.value = null;
		}
	}

	// Overrides parent
	function clearValue() {
		previewSrc.value = null;
		submitValue.value = Array.isArray(submitValue.value) ? [] : undefined
		nextTick(function () {
			validate();
		});
		emit('clear')
	}
</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
/* ==========================================================================
   Mixins
   ========================================================================== */
.form-input {
  width: 100%;
  position: relative;
}
.form-input .form-input__content {
  width: 100%;
  min-height: 38px;
  box-sizing: border-box;
  border: 1px solid #d8d8d8;
  border-radius: 5px;
  background-color: #ffffff;
  display: flex;
  position: relative;
  align-items: center;
}
.form-input .form-input__content > label, .form-input .form-input__content > .form-input__field-wrapper, .form-input .form-input__content > .dropdown, .form-input .form-input__content > .dropdown > .dropdown__activator {
  height: 100%;
  width: 100%;
  min-height: inherit;
  display: flex;
  align-items: center;
  position: relative;
}
.form-input .form-input__content .form-input__label {
  position: absolute;
  font-size: 15px;
  top: 10px;
  line-height: 1.2;
  left: 4px;
  background: #ffffff;
  color: #5e6b6b;
  padding: 0 4px;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  transform-origin: top left;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.form-input .form-input__content .form-input__field {
  width: 100%;
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1.2;
  min-height: 38px;
  padding: 4px 8px;
  border: none;
  outline: none;
  color: #525e5e;
  background-color: #ffffff;
  border-radius: 5px;
  display: flex;
  align-items: center;
}
.form-input .form-input__content .form-input__field .form-input__placeholder {
  color: #7D8484;
}
.form-input .form-input__content .form-input__field::-webkit-input-placeholder { /* Chrome/Opera/Safari */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field::-moz-placeholder { /* Firefox 19+ */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field:-ms-input-placeholder { /* IE 10+ */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field:-moz-placeholder { /* Firefox 18- */
  color: #7D8484;
  opacity: 1;
}
.form-input .form-input__content .form-input__field[type=search] {
  -webkit-appearance: none;
}
.form-input .form-input__content .form-input__field::-webkit-search-cancel-button {
  display: none;
}
.form-input .form-input__content .form-input__field-content {
  font-family: nunito-sans, Arial, Helvetica, sans-serif;
  font-size: 15px;
  line-height: 1.2;
  color: #525e5e;
}
.form-input .form-input__details {
  display: flex;
  width: 100%;
  height: 16px;
  margin-bottom: 8px;
  position: relative;
}
.form-input .form-input__details > .form-input__message {
  font-size: 11px;
  line-height: 1.1;
  text-align: left;
  font-weight: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 1px 8px;
}
.form-input .form-input__details > .form-input__message:focus, .form-input .form-input__details > .form-input__message:hover {
  overflow: unset;
  text-overflow: unset;
  white-space: unset;
  position: absolute;
  z-index: 1;
  background: #ffffff;
}
.form-input.is-active .form-input__content {
  border-color: #008200;
}
.form-input.is-required .form-input__label:after {
  content: " *";
  color: #FA5555;
}
.form-input.is-disabled {
  opacity: 0.8;
}
.form-input.is-disabled .form-input__field {
  cursor: not-allowed;
}
.form-input:not(.is-empty) .form-input__content .form-input__label, .form-input.is-active .form-input__content .form-input__label {
  left: 4px !important;
  transform: translateY(-18px) scale(0.7);
  max-width: 120%;
}
.form-input:not(.is-empty).has-suffix .form-input__content .form-input__label, .form-input.is-active.has-suffix .form-input__content .form-input__label {
  padding-right: 0;
}
.form-input.has-success .form-input__content {
  border-color: #47a447 !important;
}
.form-input.has-success .form-input__message {
  color: #47a447;
}
.form-input.has-success .form-input__label {
  color: #47a447;
}
.form-input.has-error .form-input__content {
  border-color: #FA5555 !important;
}
.form-input.has-error .form-input__message {
  color: #FA5555;
}
.form-input.has-error .form-input__label {
  color: #FA5555;
}
.form-input.has-warning .form-input__content {
  border-color: #EB9E05 !important;
}
.form-input.has-warning .form-input__message {
  color: #EB9E05;
}
.form-input.has-warning .form-input__label {
  color: #EB9E05;
}
.form-input.has-prefix .form-input__content .form-input__field-wrapper {
  padding-left: 32px;
}
.form-input.has-prefix .form-input__content .form-input__label {
  left: 32px;
}
.form-input.has-prefix .form-input__prefix {
  position: absolute;
  top: 20px;
  transform: translateY(-50%);
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
.form-input.has-prefix .form-input__prefix .icon, .form-input.has-prefix .form-input__prefix .icon > svg {
  width: 18px;
  height: 18px;
}
.form-input.has-suffix .form-input__content .form-input__field-wrapper, .form-input.has-suffix .form-input__content .form-input__label {
  padding-right: 32px;
}
.form-input.has-suffix .form-input__suffix {
  position: absolute;
  top: 20px;
  transform: translateY(-50%);
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
.form-input.has-suffix .form-input__suffix .icon, .form-input.has-suffix .form-input__suffix .icon > svg {
  width: 18px;
  height: 18px;
  color: #7D8484;
}
.form-input.is-inverted .form-input__content {
  border-color: #f4f4f5;
  background-color: #f4f4f5;
}
.form-input.is-inverted .form-input__content .form-input__field {
  color: #525e5e;
  background-color: #f4f4f5;
}
.form-input.is-inverted .form-input__content .form-input__field .form-input__placeholder {
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field::-webkit-input-placeholder { /* Chrome/Opera/Safari */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field::-moz-placeholder { /* Firefox 19+ */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field:-ms-input-placeholder { /* IE 10+ */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__field:-moz-placeholder { /* Firefox 18- */
  color: #465050;
  opacity: 0.8;
}
.form-input.is-inverted .form-input__content .form-input__label {
  background: transparent;
}
.form-input.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.is-plain .form-input__content {
  border: none;
}
.form-input.is-plain .form-input__content .form-input__field {
  padding: 0 !important;
}
.form-input.is-plain .form-input__details .form-input__message {
  padding-left: 0;
  padding-right: 0;
}
.form-input.form-input-xs .form-input__content {
  min-height: 24px;
}
.form-input.form-input-xs .form-input__content .form-input__label {
  font-size: 11.5px;
  top: 5.1px;
}
.form-input.form-input-xs .form-input__content .form-input__field {
  min-height: 24px;
  font-size: 11.5px;
}
.form-input.form-input-xs .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-xs.has-prefix .form-input__prefix {
  top: 13px;
}
.form-input.form-input-xs.has-suffix .form-input__suffix {
  top: 13px;
}
.form-input.form-input-xs:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xs.is-active .form-input__content .form-input__label {
  transform: translateY(-11px) scale(0.7);
}
.form-input.form-input-xs.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xs.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-17px) scale(0.7);
}
.form-input.form-input-sm .form-input__content {
  min-height: 32px;
}
.form-input.form-input-sm .form-input__content .form-input__label {
  font-size: 12px;
  top: 8.8px;
}
.form-input.form-input-sm .form-input__content .form-input__field {
  min-height: 32px;
  font-size: 12px;
}
.form-input.form-input-sm .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-sm.has-prefix .form-input__prefix {
  top: 17px;
}
.form-input.form-input-sm.has-suffix .form-input__suffix {
  top: 17px;
}
.form-input.form-input-sm:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-sm.is-active .form-input__content .form-input__label {
  transform: translateY(-15px) scale(0.7);
}
.form-input.form-input-sm.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-sm.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-21px) scale(0.7);
}
.form-input.form-input-md .form-input__content {
  min-height: 38px;
}
.form-input.form-input-md .form-input__content .form-input__label {
  font-size: 15px;
  top: 10px;
}
.form-input.form-input-md .form-input__content .form-input__field {
  min-height: 38px;
  font-size: 15px;
}
.form-input.form-input-md .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-md.has-prefix .form-input__prefix {
  top: 20px;
}
.form-input.form-input-md.has-suffix .form-input__suffix {
  top: 20px;
}
.form-input.form-input-md:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-md.is-active .form-input__content .form-input__label {
  transform: translateY(-18px) scale(0.7);
}
.form-input.form-input-md.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-md.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.form-input-lg .form-input__content {
  min-height: 46px;
}
.form-input.form-input-lg .form-input__content .form-input__label {
  font-size: 15px;
  top: 14px;
}
.form-input.form-input-lg .form-input__content .form-input__field {
  min-height: 46px;
  font-size: 15px;
}
.form-input.form-input-lg .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-lg.has-prefix .form-input__prefix {
  top: 24px;
}
.form-input.form-input-lg.has-suffix .form-input__suffix {
  top: 24px;
}
.form-input.form-input-lg:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-lg.is-active .form-input__content .form-input__label {
  transform: translateY(-22px) scale(0.7);
}
.form-input.form-input-lg.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-lg.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-28px) scale(0.7);
}
.form-input.form-input-xl .form-input__content {
  min-height: 50px;
}
.form-input.form-input-xl .form-input__content .form-input__label {
  font-size: 16px;
  top: 15.4px;
}
.form-input.form-input-xl .form-input__content .form-input__field {
  min-height: 50px;
  font-size: 16px;
}
.form-input.form-input-xl .form-input__content textarea.form-input__field {
  min-height: auto;
}
.form-input.form-input-xl.has-prefix .form-input__prefix {
  top: 26px;
}
.form-input.form-input-xl.has-suffix .form-input__suffix {
  top: 26px;
}
.form-input.form-input-xl:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xl.is-active .form-input__content .form-input__label {
  transform: translateY(-24px) scale(0.7);
}
.form-input.form-input-xl.is-inverted:not(.is-empty) .form-input__content .form-input__label, .form-input.form-input-xl.is-inverted.is-active .form-input__content .form-input__label {
  transform: translateY(-30px) scale(0.7);
}
</style>